import React, { useEffect } from 'react'
import NotFoundPage from '../NotFoundPage'
import { SurveyFeature } from '../../features/SurveyFeature'
import { useParams } from 'react-router-dom'
import PAGE_TITLES from '../../consts/titles'
import CustomHeader from '../../components/CustomHeader'
import { useAppDispatch } from '../../store'
import SkipNavigation from '../../components/SkipNavigation'
import { getStudentSurveyById, getStudentSurveyByIdPublic } from '../../store/slices/studentSurveyListSlice/apis'
import { getIsLoading, getIsSubmitting, getPublicSurveyLoadError } from '../../store/slices/studentSurveyListSlice/selectors'
import Loader from '../../components/Loader'

import { Alert, Container } from '@mui/material'

const SurveyPage: React.FC = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const { id = '', tenantToken = '', distributionId = '' } = useParams()
  const queryParams = new URLSearchParams(location.search)
  const isPublic = queryParams.get('isPublic')
  const hasPublicTenantRoute = tenantToken !== '' && distributionId !== ''
  const isPublicSurvey = isPublic === 'true' || hasPublicTenantRoute

  const loading = getIsLoading() ?? false
  const submitting = getIsSubmitting() ?? false
  const publicSurveyLoadError = getPublicSurveyLoadError()

  useEffect(() => {
    const hasTenantPublic = isPublicSurvey && tenantToken !== '' && distributionId !== ''
    const hasLegacyPublic = isPublicSurvey && id !== '' && !hasTenantPublic

    if (hasTenantPublic) {
      void dispatch(getStudentSurveyByIdPublic({
        url: `/api/surveys/${tenantToken}/${distributionId}/public/`
      }))
      return
    }
    if (hasLegacyPublic) {
      void dispatch(getStudentSurveyByIdPublic({ urlId: id }))
      return
    }
    if (id !== '') {
      void dispatch(getStudentSurveyById({ urlId: id }))
    }
  }, [id, tenantToken, distributionId, dispatch, isPublicSurvey])

  const hasRouteParams = (tenantToken !== '' && distributionId !== '') || id !== ''
  if (!hasRouteParams) {
    return <NotFoundPage />
  }

  if (loading) {
    return <Loader open={loading} />
  }

  if (isPublicSurvey && publicSurveyLoadError != null && publicSurveyLoadError !== '') {
    return (
      <>
        <SkipNavigation />
        <CustomHeader title={PAGE_TITLES.SURVEYS} />
        <Container sx={{ marginTop: 5, marginBottom: 18 }}>
          <Alert severity="error" role="alert">
            {publicSurveyLoadError}
          </Alert>
        </Container>
      </>
    )
  }

  return (
    <>
      <SkipNavigation />
      <CustomHeader title={PAGE_TITLES.SURVEYS} />
      {/*
        CR-260: the submit loader is an overlay, not a branch that replaces the survey.
        Swapping `SurveyFeature` out mid-submit unmounted it, and the remount on success
        re-ran its draft-restore effect — re-showing "Reloading previous responses" right
        after submission — while a failed submit lost the in-progress form and step.
      */}
      <Loader open={submitting} />
      <Container sx={{ marginTop: isPublicSurvey ? 5 : 0, marginBottom: isPublicSurvey ? 18 : undefined }}>
        <SurveyFeature isPublic={isPublicSurvey || undefined} />
      </Container>
    </>
  )
}

export default SurveyPage
